import React from 'react';
import * as Lucide from 'lucide-react';
import { Building2 } from 'lucide-react';
import { BOOSTER_FEATURES, BOOSTER_SPECS, BOOSTER_ENV, IDEAL_FOR } from '../mock';

const LightIcon = ({ name, size = 40, iconSize = 20 }) => {
  const Icon = Lucide[name] || Lucide.Circle;
  return (
    <div
      className="inline-flex items-center justify-center rounded-full bg-[#FFF3E4] text-[#F5901F] shrink-0"
      style={{ width: `clamp(${size * 0.75}px, ${size * 0.09}vw, ${size}px)`, height: `clamp(${size * 0.75}px, ${size * 0.09}vw, ${size}px)` }}
      aria-hidden="true"
    >
      <Icon size={iconSize} strokeWidth={2.2} style={{ width: '52%', height: '52%' }} />
    </div>
  );
};

const BoosterSection = () => {
  return (
    <section id="booster" className="light-section-alt relative py-8 md:py-12 overflow-hidden">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Header — same style as Product Overview heading */}
        <div className="reveal-on-scroll mb-8">
          <h2 className="font-display font-black text-[clamp(1.75rem,4.2vw,3.75rem)] leading-tight text-navy mt-2">
            PORTABLE BOOSTER – <span className="orange-accent">RANGE EXTENSION</span>
          </h2>
          <div className="text-navy-70 mt-3 text-[clamp(0.8rem,1.1vw,1rem)]">Range Extension. Mission Multiplier.</div>
          <div className="mt-4 h-1 w-16 rounded orange-bar" />
        </div>

        {/* Features + How It Works — 1/3 left cards, 2/3 right visual, no excess whitespace */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-5 mt-10 lg:items-stretch">
          {/* Left: feature cards — natural height, plus a filler stat panel that grows to match the image column */}
          <div className="flex flex-col gap-4 lg:gap-6 h-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 lg:gap-6">
              {BOOSTER_FEATURES.map((f, i) => (
                <div
                  key={f.title}
                  className="light-card light-card-hover rounded-xl p-6 flex items-start gap-4 transition-all duration-300 hover:-translate-y-0.5 reveal-on-scroll"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <LightIcon name={f.icon} size={52} iconSize={24} />
                  <div>
                    <div className="text-[#F5901F] font-bold text-[clamp(0.85rem,1.3vw,1.125rem)] tracking-wide">{f.title}</div>
                    <div className="text-navy-70 text-[clamp(0.75rem,1.05vw,1rem)] mt-1.5 leading-relaxed">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Filler panel: condensed specs, grows to absorb leftover vertical space in the left column */}
            <div className="light-card rounded-xl overflow-hidden flex-1 flex flex-col reveal-on-scroll">
              <div className="flex items-center gap-2.5 px-6 py-4 border-b border-[#E6EAF0]">
                <LightIcon name="Radio" size={36} iconSize={18} />
                <span className="font-bold text-navy tracking-wide text-[clamp(0.75rem,1.05vw,1rem)]">PORTABLE BOOSTER – SPECIFICATIONS</span>
              </div>
              <div className="flex-1 flex flex-col justify-center divide-y divide-[#E6EAF0]">
                {BOOSTER_SPECS.map((row, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-4 px-6 py-3">
                    <span className="text-navy font-semibold text-[clamp(0.65rem,0.95vw,0.875rem)] shrink-0">{row.feature}</span>
                    <span className="text-navy-70 text-[clamp(0.65rem,0.95vw,0.875rem)] text-right">{row.spec}</span>
                  </div>
                ))}
              </div>
              <div className="px-6 py-3 text-[clamp(0.55rem,0.75vw,0.625rem)] text-navy-55 border-t border-[#E6EAF0]">*LOS (Line-of-Sight). Actual range depends on terrain, environment, antenna height and operating conditions.</div>
            </div>
          </div>

          {/* Right: How It Works — fills remaining 2/3, stretches to match left column height */}
          <div className="reveal-on-scroll h-full">
            <div
              className="rounded-xl overflow-hidden h-full flex flex-col"
              style={{ background: 'radial-gradient(60% 60% at 50% 55%, #0f2440 0%, #06101E 65%, #050a13 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              {/* Header */}
              <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10">
                <div className="h-1 w-8 orange-bar rounded" />
                <span className="font-black text-white tracking-wider">HOW IT WORKS</span>
              </div>

              {/* Dark canvas with image + animated signals */}
              <div className="relative w-full overflow-hidden">
                {/* Animated SVG signal lines radiating from center */}
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none z-10"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  {/* Lines from center booster to each corner scene */}
                  {[
                    { x2: 18, y2: 30 },
                    { x2: 72, y2: 20 },
                    { x2: 78, y2: 62 },
                    { x2: 20, y2: 72 },
                    { x2: 78, y2: 80 },
                  ].map((p, i) => (
                    <line
                      key={i}
                      x1="50" y1="50"
                      x2={p.x2} y2={p.y2}
                      stroke="#F5901F"
                      strokeWidth="0.4"
                      strokeDasharray="2,1.5"
                      opacity="0.65"
                      style={{ animation: 'beamDash 1.8s linear infinite', animationDelay: `${i * 0.3}s` }}
                    />
                  ))}
                </svg>

                {/* Signal rings at center (booster position ~50% 48%) */}
                <div className="absolute z-10 pointer-events-none" style={{ top: '44%', left: '47%', transform: 'translate(-50%,-50%)' }}>
                  {[0, 0.8, 1.6].map((delay, i) => (
                    <span
                      key={i}
                      className="absolute rounded-full border-2 border-[#F5901F]"
                      style={{
                        width: `${40 + i * 18}px`,
                        height: `${40 + i * 18}px`,
                        top: '50%', left: '50%',
                        transform: 'translate(-50%,-50%)',
                        animation: 'signalRing 2.4s ease-out infinite',
                        animationDelay: `${delay}s`,
                        opacity: 0.7,
                      }}
                    />
                  ))}
                </div>

                {/* The image itself */}
                <img
                  src="/how_it_works.png"
                  alt="How the HERTZX Portable Booster works"
                  className="relative w-full h-auto object-contain z-0"
                  loading="lazy"
                />

                {/* Bottom glow */}
                <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none z-10" style={{
                  background: 'radial-gradient(60% 100% at 50% 100%, rgba(245,144,31,0.25), transparent 70%)'
                }} />
              </div>

              {/* Context scenes strip below the diagram — flex-1 so it absorbs any leftover height instead of leaving a white gap under the card */}
              <div className="px-5 py-5 border-t border-white/10 flex-1 flex flex-col justify-center" style={{ background: '#06101E' }}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5901F]" />
                  <span className="text-white/70 text-xs font-bold tracking-widest uppercase">Real-World Deployment Scenarios</span>
                </div>
                <div className="relative rounded-xl overflow-hidden ring-1 ring-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
                  <img
                    src="/below_how_it_works.png"
                    alt="HERTZX Booster deployment scenarios – Defence, Navy, Industrial, Firefighting"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                  {/* Subtle overlay for polish */}
                  <div className="absolute inset-0 pointer-events-none" style={{
                    background: 'linear-gradient(to bottom, transparent 60%, rgba(6,16,30,0.6) 100%)'
                  }} />
                </div>
                <p className="text-white/45 text-[10px] mt-2 text-right tracking-wide">
                  *Applicable across defence, navy, industrial and emergency response environments
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default BoosterSection;